import { count } from '@wellread/core';
import { Link, Outlet, useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { partOf } from '../lib/feedback';
import { scrollPageToTop } from '../lib/scroll';
import { settingsOf } from '../lib/settings';
import { signOut } from '../lib/session';
import { useDueEverywhere, useMe, usePatchSettings } from '../lib/queries';
import { dismissReady, useReadyAnswer } from '../state/answers';
import { useChrome } from '../state/chrome';
import { useUpload, useUploadTo } from '../state/upload';
import { AccountMenu, AppBar, cx, Icon, type IconName, initials, Logo, TabBar, TabIcon } from '../ui';
import { ReadyNotice } from './AskDock';
import { Feedback } from './Feedback';
import { Gate } from './Gate';
import { Palette } from './Palette';
import { UploadStatus } from './UploadStatus';
import { useBootstrap } from './useBootstrap';

type Section = 'home' | 'library' | 'notes' | 'explore' | 'map' | 'settings';

/** Which place a page belongs to. A book's own pages, a track's, and adding a book are the library's; a wrong address is nowhere. */
function sectionOf(path: string): Section | undefined {
  if (path === '/') return 'home';
  if (path === '/notes') return 'notes';
  if (path.startsWith('/explore')) return 'explore';
  if (path.startsWith('/map')) return 'map';
  if (path.startsWith('/settings')) return 'settings';
  return /^\/(library|books|tracks|import)(\/|$)/.test(path) ? 'library' : undefined;
}

/**
 * The places, in one order on the top bar, the phone's tab bar and the
 * Apple apps: Explore, the tab bar's hero, in the middle. Review follows them
 * on the top bar (ReviewLink); the tab bar keeps to these five.
 */
const NAV: {
  id: Section;
  label: string;
  to: '/' | '/library' | '/notes' | '/explore' | '/map';
  icon: IconName;
}[] = [
  { id: 'home', label: 'Home', to: '/', icon: 'home' },
  { id: 'library', label: 'Library', to: '/library', icon: 'library' },
  { id: 'explore', label: 'Explore', to: '/explore', icon: 'explore' },
  { id: 'notes', label: 'Notes', to: '/notes', icon: 'notes' },
  { id: 'map', label: 'Map', to: '/map', icon: 'map' },
];

/** The top bar and the page. Reader and session are full-screen and live elsewhere. */
export function Shell() {
  return (
    <Gate>
      <Frame>
        <Outlet />
      </Frame>
    </Gate>
  );
}

/** The same frame around a page with no route of its own: the one a wrong address gets. */
export function Framed({ children }: { children: ReactNode }) {
  return (
    <Gate>
      <Frame>{children}</Frame>
    </Gate>
  );
}

function Frame({ children }: { children: ReactNode }) {
  const path = useRouterState({ select: (r) => r.location.pathname });
  const section = sectionOf(path);
  const setPalette = useChrome((s) => s.setPalette);
  useBootstrap();
  // The place you are on, pressed again, goes back to the top of its page.
  const toTop = (to: string) => (path === to ? scrollPageToTop : undefined);

  return (
    <div className="app">
      <AppBar
        brand={
          <Link to="/" className="brand" aria-label="wellread, home">
            <Logo />
          </Link>
        }
        nav={
          <>
            {NAV.map((n) => (
              <Link
                key={n.id}
                to={n.to}
                className={cx(section === n.id && 'on')}
                aria-current={section === n.id ? 'page' : undefined}
                onClick={toTop(n.to)}
              >
                {n.label}
              </Link>
            ))}
            <ReviewLink />
          </>
        }
        review={<ReviewLink className="barreview" />}
        onSearch={() => setPalette(true)}
        feedback={<Feedback where="bar" />}
        account={<Account />}
      />
      <main className="main" id="main">
        <UploadStatus />
        {children}
      </main>
      <TabBar>
        {NAV.map((n) => (
          <Link
            key={n.id}
            to={n.to}
            className={cx(section === n.id && 'on')}
            aria-current={section === n.id ? 'page' : undefined}
            onClick={toTop(n.to)}
          >
            <TabIcon name={n.icon} hero={n.id === 'explore'} />
            {n.label}
          </Link>
        ))}
      </TabBar>
      <DropZone />
      <UploadToast />
      <ReadyToast />
      <Palette />
    </div>
  );
}

/**
 * Review, from any page: a session across every book, with how many ideas it
 * would ask now when some are due. Never the page you're on: a session has
 * the whole screen, with no bar.
 */
function ReviewLink({ className }: { className?: string }) {
  const due = useDueEverywhere().data ?? 0;
  return (
    <Link
      to="/review/session"
      className={className}
      aria-label={due ? `Review, ${count(due)} due` : undefined}
    >
      Review
      {due ? <span className="n">{count(due)}</span> : null}
    </Link>
  );
}

/** Where a reader writes to for help, or to say what they think. */
const SUPPORT = 'hello@wellread.ink';

/** The avatar and the menu it opens. Closes on a click outside, Escape, or a move to another page. */
function Account() {
  const me = useMe();
  const patch = usePatchSettings();
  const navigate = useNavigate();
  const openFeedback = useChrome((s) => s.openFeedback);
  const path = useRouterState({ select: (r) => r.location.pathname });
  // Open on the page it was opened on: moving to another page closes it.
  const [openOn, setOpenOn] = useState<string | null>(null);
  const open = openOn === path;
  const setOpen = (o: boolean) => setOpenOn(o ? path : null);
  const box = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const settings = settingsOf(me.data);
  const email = me.data?.email ?? '';
  const name = settings.profile.display_name || email.split('@')[0] || '';

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpenOn(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpenOn(null);
      button.current?.focus();
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="acct" ref={box}>
      <button
        ref={button}
        type="button"
        className={cx('avatar', open && 'on')}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account"
        onClick={() => setOpen(!open)}
      >
        {initials(name)}
      </button>
      {open ? (
        <AccountMenu
          name={name}
          email={email}
          theme={settings.chrome.theme}
          onTheme={(theme) => patch.mutate({ chrome: { theme } })}
        >
          <Link to="/settings/$tab" params={{ tab: 'reading' }} role="menuitem" className="item">
            <Icon name="settings" size={15} />
            Settings
          </Link>
          {/* A narrow window's top bar has no room for Feedback: it is here instead. */}
          <button
            type="button"
            role="menuitem"
            className="item acct-fb"
            onClick={() => {
              // Closed, the panel gives the keyboard back to the avatar.
              button.current?.focus();
              setOpenOn(null);
              openFeedback(path);
            }}
          >
            <Icon name="feedback" size={15} />
            {partOf(path).title}
          </button>
          <a href={`mailto:${SUPPORT}`} role="menuitem" className="item">
            <Icon name="help" size={15} />
            Email us
          </a>
          <button
            type="button"
            role="menuitem"
            className="item"
            onClick={() => void signOut().then(() => navigate({ to: '/welcome' }))}
          >
            <Icon name="signout" size={15} />
            Sign out
          </button>
        </AccountMenu>
      ) : null}
    </div>
  );
}

/** Drop books (EPUBs and PDFs, one or several) anywhere on the page. */
function DropZone() {
  // How many files are over the page, while they are: 0 when none.
  const [over, setOver] = useState(0);
  const upload = useUploadTo();
  // Dropped on a track's page, the books are for that track.
  const { trackId } = useParams({ strict: false });
  useEffect(() => {
    let depth = 0;
    const hasFiles = (e: DragEvent) => !!e.dataTransfer?.types.includes('Files');
    const enter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth++;
      // A browser that doesn't say how many until the drop counts as one.
      setOver(Math.max(1, Array.from(e.dataTransfer?.items ?? []).filter((i) => i.kind === 'file').length));
    };
    const leave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) setOver(0);
    };
    const over = (e: DragEvent) => {
      if (hasFiles(e)) e.preventDefault();
    };
    const drop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth = 0;
      setOver(0);
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length) void upload(files, trackId);
    };
    window.addEventListener('dragenter', enter);
    window.addEventListener('dragleave', leave);
    window.addEventListener('dragover', over);
    window.addEventListener('drop', drop);
    return () => {
      window.removeEventListener('dragenter', enter);
      window.removeEventListener('dragleave', leave);
      window.removeEventListener('dragover', over);
      window.removeEventListener('drop', drop);
    };
  }, [upload, trackId]);
  return over ? (
    <div className="dropzone">{over > 1 ? 'Drop the books to add them' : 'Drop the book to add it'}</div>
  ) : null;
}

/** Why an upload didn't go, for a moment. How it is going is on the page (UploadStatus). */
function UploadToast() {
  const error = useUpload((s) => s.error);
  const clear = useUpload((s) => s.clear);
  useEffect(() => {
    if (!error) return;
    const t = window.setTimeout(clear, 6000);
    return () => window.clearTimeout(t);
  }, [error, clear]);
  return error ? (
    <div className="toast" role="alert">
      {error}
    </div>
  ) : null;
}

/** An answer asked for earlier is ready, said on whichever page the reader is on. The reader has its own, in its corner. */
function ReadyToast() {
  const ready = useReadyAnswer();
  return ready ? (
    <ReadyNotice toast conversationId={ready.id} failed={ready.failed} onDismiss={dismissReady} />
  ) : null;
}
